"use client";

import React, { useEffect, useState } from "react";
import { Globe, Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex items-center gap-x-8">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-4 w-20 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="hidden md:flex items-center gap-x-8">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="relative py-2 text-sm font-medium font-default uppercase tracking-widest text-foreground transition-colors hover:text-primary after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-center after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:scale-x-100">
          {item.label}
        </Link>)}
    </nav>;
}
function MobileNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <Button variant="ghost" size="icon" className="md:hidden text-foreground">
        <Menu className="size-5" />
      </Button>;
  }
  return <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-accent/20">
          <Menu className="size-6" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-background border-border/50 shadow-xl">
        {menuItems.map((item, i) => <DropdownMenuItem key={item.id} asChild data-index={i} className="focus:bg-accent/20">
            <Link to={item.href || "#"} className="w-full cursor-pointer py-3 text-sm font-medium font-default uppercase tracking-widest text-foreground">
              {item.label}
            </Link>
          </DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section data-section-id="221732" id="header" className={`sticky top-0 z-50 w-full transition-all duration-400 bg-background text-foreground border-b border-border/50 ${isScrolled ? "bg-background/80 backdrop-blur-xl border-b border-border/20 shadow-sm" : ""}`}>
      <div className="container mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/HomePage" className="flex items-center">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1662466/img/nexus_logo.png" alt="NEXUS Logo" className="h-8 w-auto" />
          </Link>
        </div>

        {}
        <MenuProvider menu_id="16">
          <DesktopNav />
        </MenuProvider>

        {}
        <div className="flex items-center gap-2 md:gap-6">
          <Button variant="ghost" size="icon" className="hidden md:inline-flex text-foreground hover:text-primary hover:bg-accent/10" aria-label="Select Language">
            <Globe className="size-5" />
          </Button>
          
          <Button className="hidden md:inline-flex bg-primary text-primary-foreground hover:bg-primary/90 font-default font-semibold tracking-wide shadow-md rounded-md">
            Start Diagnostic
          </Button>

          {}
          <MenuProvider menu_id="16">
            <MobileNav />
          </MenuProvider>
        </div>
      </div>
    </section>;
}